import React from 'react';
import { Popup } from '@gravity-ui/uikit';
import { keydownHandler } from 'prosemirror-keymap';
import { Plugin, TextSelection } from 'prosemirror-state';
import { findChildrenByMark, findParentNode } from 'prosemirror-utils';
import { Decoration, DecorationSet } from 'prosemirror-view';
import { LinkAttr, linkType, normalizeUrlFactory } from '../../../../extensions/markdown';
import { ErrorLoggerBoundary } from '../../../../react-utils/ErrorBoundary';
import { findMark, isMarkActive } from '../../../../utils/marks';
import { getReactRendererFromState } from '../../ReactRenderer';
import { LinkForm } from './TooltipView';
const className = 'pm-link-focus-deco';
const placement = ['bottom-start', 'bottom-end', 'top-start', 'top-end'];
function getTextNode(state) {
    if (!(state.selection instanceof TextSelection) ||
        !state.selection.empty ||
        !isMarkActive(state, linkType(state.schema)))
        return null;
    const parent = findParentNode(Boolean)(state.selection);
    if (!parent) {
        return null;
    }
    const textNodes = findChildrenByMark(parent === null || parent === void 0 ? void 0 : parent.node, linkType(state.schema));
    const textNode = textNodes.find((n) => {
        const start = n.pos + parent.pos;
        const end = start + n.node.nodeSize;
        return start <= state.selection.from && end >= state.selection.from;
    });
    if (!textNode)
        return null;
    const absolutePos = textNode.pos + parent.pos + 1;
    return Object.assign(Object.assign({}, textNode), { absolutePos,
        parent, from: absolutePos, to: absolutePos + textNode.node.nodeSize });
}
export const linkTooltipPlugin = (deps) => {
    let viewTooltip = null;
    return new Plugin({
        view(view) {
            viewTooltip = new SelectionTooltip(view, deps);
            return viewTooltip;
        },
        props: {
            handleKeyDown: keydownHandler({
                Escape: () => { var _a; return (_a = viewTooltip === null || viewTooltip === void 0 ? void 0 : viewTooltip.onEscapeDown()) !== null && _a !== void 0 ? _a : false; },
            }),
            decorations(state) {
                const textNode = getTextNode(state);
                if (!textNode)
                    return DecorationSet.empty;
                const { from, to } = textNode;
                return DecorationSet.create(state.doc, [
                    Decoration.inline(from, to, {
                        class: `pm-node-selected ${className}`,
                    }),
                ]);
            },
        },
    });
};
class SelectionTooltip {
    constructor(view, deps) {
        this.isTooltipOpen = false;
        this.manualHidden = false;
        this.selectionTooltipProps = { show: false };
        this.onOutisdeClick = () => {
            // after all updates of the editor state
            setTimeout(() => {
                if (!this.view.hasFocus()) {
                    this.removePlaceholderLink(this.textNode);
                    this.hideTooltip();
                    this.manualHidden = false;
                }
            });
        };
        this.normalizeUrl = normalizeUrlFactory(deps);
        this.view = view;
        this.update(view, null);
    }
    update(view, prevState) {
        if (!view.dom.parentNode) {
            this.hideTooltip();
            return;
        }
        const { state } = view;
        if (prevState && prevState.doc.eq(state.doc) && prevState.selection.eq(state.selection))
            return;
        const prevTextNode = this.textNode;
        this.textNode = getTextNode(view.state);
        const prevRef = this.textNodeRef;
        this.updateTextNodeRef();
        if (!this.textNode || !this.textNodeRef) {
            this.removePlaceholderLink(prevTextNode);
            this.hideTooltip();
            return;
        }
        if (prevRef !== this.textNodeRef) {
            this.manualHidden = false;
        }
        if (this.manualHidden) {
            this.hideTooltip();
        }
        else {
            this.renderTooltip({
                show: true,
                domElem: this.textNodeRef,
                onCancel: () => this.cancelPopup(),
                attrs: this.getMarkAttrs(),
                onChange: this.changeAttrs.bind(this),
                onOutsideClick: this.onOutisdeClick,
            });
        }
    }
    destroy() {
        var _a;
        this.isTooltipOpen = false;
        this.selectionTooltipProps = { show: false };
        (_a = this.renderItem) === null || _a === void 0 ? void 0 : _a.remove();
        this.renderItem = undefined;
    }
    onEscapeDown() {
        if (this.isTooltipOpen) {
            this.removePlaceholderLink(this.textNode);
            this.manualHidden = true;
            this.hideTooltip();
            return true;
        }
        return false;
    }
    updateTextNodeRef() {
        const decoElem = this.view.dom.getElementsByClassName(className)[0];
        this.textNodeRef = decoElem;
    }
    getMarkAttrs() {
        const { textNode } = this;
        const linkMark = textNode && findMark(textNode.node, linkType(this.view.state.schema));
        return linkMark === null || linkMark === void 0 ? void 0 : linkMark.attrs;
    }
    hideTooltip() {
        this.renderTooltip({ show: false });
    }
    renderTooltip(props) {
        var _a;
        this.isTooltipOpen = props.show;
        this.selectionTooltipProps = props;
        this.renderItem = (_a = this.renderItem) !== null && _a !== void 0 ? _a : this.createRenderItem();
        this.renderItem.rerender();
    }
    cancelPopup() {
        this.removePlaceholderLink(this.textNode, { returnSelection: true });
        this.manualHidden = true;
        this.hideTooltip();
        this.view.focus();
    }
    removePlaceholderLink(textNode, opts) {
        var _a;
        if (textNode) {
            const { view } = this;
            const { state } = view;
            const attrs = (_a = findMark(textNode.node, linkType(state.schema))) === null || _a === void 0 ? void 0 : _a.attrs;
            if (attrs === null || attrs === void 0 ? void 0 : attrs[LinkAttr.IsPlaceholder]) {
                const { from, to } = textNode;
                let tr = state.tr.removeMark(from, to, linkType(state.schema));
                if (opts === null || opts === void 0 ? void 0 : opts.returnSelection) {
                    tr = tr.setSelection(TextSelection.create(tr.doc, from, to));
                }
                view.dispatch(tr);
            }
        }
    }
    changeAttrs({ href }) {
        const { view, textNode } = this;
        if (!textNode)
            return;
        const normalizeResult = this.normalizeUrl(href);
        if (normalizeResult) {
            const { url } = normalizeResult;
            const { from, to } = textNode;
            view.dispatch(view.state.tr.addMark(from, to, linkType(view.state.schema).create({ href: url })));
            setTimeout(this.cancelPopup.bind(this));
        }
    }
    createRenderItem() {
        return getReactRendererFromState(this.view.state).createItem('link-tooltip', () => (React.createElement(ErrorLoggerBoundary, null,
            React.createElement(SelectionTooltipView, Object.assign({}, this.selectionTooltipProps)))));
    }
}
const SelectionTooltipView = (props) => {
    const { show } = props;
    if (!show)
        return null;
    const { domElem, onChange, onCancel, onOutsideClick, attrs = {} } = props;
    const href = attrs[LinkAttr.Href];
    const autoFocus = attrs[LinkAttr.IsPlaceholder];
    return (React.createElement(Popup, { key: href, open: true, anchorRef: { current: domElem }, placement: placement, onEscapeKeyDown: onCancel, onOutsideClick: onOutsideClick },
        React.createElement(LinkForm, { href: href !== null && href !== void 0 ? href : '', autoFocus: autoFocus, onChange: onChange, onCancel: onCancel })));
};
